import React, { useEffect, useRef, useState } from 'react';
import { Shield, CheckCircle, Truck, Phone, Award, Clock, MapPin, Star } from 'lucide-react';

/**
 * TrustBlock Premium — Section "Pourquoi nous choisir"
 * Cartes animées au scroll · Icônes colorées · Effet hover
 */

const TRUST_ITEMS = [
  {
    icon: <Star size={28} fill="#D4AF37" color="#D4AF37" />,
    iconBg: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)',
    iconColor: '#D4AF37',
    emoji: '⭐',
    title: '4.6 ★ avis clients',
    desc: '9 avis vérifiés · Lot, Aveyron, Cantal.',
    accent: '#D4AF37',
    delay: 0
  },
  {
    icon: <Truck size={28} />,
    iconBg: 'linear-gradient(135deg, rgba(93,188,191,0.15) 0%, rgba(93,188,191,0.05) 100%)',
    iconColor: '#5DBCBF',
    emoji: '🚚',
    title: 'Livraison & installation',
    desc: 'Nous intervenons dans un rayon de 1h30 autour de Figeac — Lot, Aveyron, Cantal, Corrèze.',
    accent: '#5DBCBF',
    delay: 80
  },
  {
    icon: <Shield size={28} />,
    iconBg: 'linear-gradient(135deg, rgba(31,157,122,0.15) 0%, rgba(31,157,122,0.05) 100%)',
    iconColor: '#1F9D7A',
    emoji: '🔒',
    title: 'Paiement sécurisé',
    desc: 'Acompte 30 % en ligne via Stripe SSL. Solde à la livraison.',
    accent: '#1F9D7A',
    delay: 160
  },
  {
    icon: <CheckCircle size={28} />,
    iconBg: 'linear-gradient(135deg, rgba(244,167,178,0.15) 0%, rgba(244,167,178,0.05) 100%)',
    iconColor: '#F4A7B2',
    emoji: '✅',
    title: 'Matériel vérifié',
    desc: 'Chaque produit est inspecté, nettoyé et testé avant chaque sortie.',
    accent: '#F4A7B2',
    delay: 240
  },
  {
    icon: <Clock size={28} />,
    iconBg: 'linear-gradient(135deg, rgba(197,139,25,0.15) 0%, rgba(197,139,25,0.05) 100%)',
    iconColor: '#C58B19',
    emoji: '🌙',
    title: 'Reprise nocturne possible',
    desc: "Après une soirée ou un concert, nous récupérons le matériel de nuit. Pas de contrainte d'horaires pour vous.",
    accent: '#C58B19',
    delay: 320
  },
  {
    icon: <Phone size={28} />,
    iconBg: 'linear-gradient(135deg, rgba(93,188,191,0.15) 0%, rgba(93,188,191,0.05) 100%)',
    iconColor: '#5DBCBF',
    emoji: '📞',
    title: 'Disponibles 24h/24 pendant l\'événement',
    desc: "En cas de panne ou problème, nous intervenons sur place dans les plus brefs délais durant votre événement.",
    accent: '#5DBCBF',
    delay: 400
  },
];

function TrustCard({ item, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.55s ease ${item.delay}ms, transform 0.55s ease ${item.delay}ms`,
        background: hovered
          ? `linear-gradient(135deg, #FFFFFF 0%, ${item.accent}08 100%)`
          : '#FFFFFF',
        border: `1.5px solid ${hovered ? item.accent + '40' : 'var(--border)'}`,
        borderRadius: '20px',
        padding: '28px 24px',
        boxShadow: hovered
          ? `0 16px 48px ${item.accent}18, 0 4px 12px rgba(26,26,46,0.05)`
          : '0 4px 16px rgba(26,26,46,0.06)',
        cursor: 'default',
        transform: visible ? (hovered ? 'translateY(-3px) scale(1.01)' : 'translateY(0)') : 'translateY(20px)',
        transition: `all 0.4s ease ${visible ? 0 : item.delay}ms`,
      }}
    >
      {/* Icône */}
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: 16,
          background: item.iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 16,
          color: item.iconColor,
          transform: hovered ? 'scale(1.08) rotate(-2deg)' : 'scale(1)',
          transition: 'transform 0.3s ease',
        }}
      >
        {item.icon}
      </div>

      {/* Texte */}
      <h3 style={{
        fontSize: '15px',
        fontWeight: 700,
        color: 'var(--ink)',
        marginBottom: 8,
        lineHeight: 1.3,
      }}>
        {item.title}
      </h3>
      <p style={{
        fontSize: '13px',
        color: 'var(--text-muted)',
        lineHeight: 1.65,
        margin: 0,
      }}>
        {item.desc}
      </p>

      {/* Accent bottom */}
      <div style={{
        height: 3,
        borderRadius: 2,
        background: `linear-gradient(90deg, ${item.accent} 0%, transparent 100%)`,
        marginTop: 16,
        opacity: hovered ? 1 : 0.4,
        transition: 'opacity 0.3s',
        width: hovered ? '70%' : '40%',
        transition: 'width 0.4s ease, opacity 0.3s ease',
      }} />
    </div>
  );
}

export default function TrustBlockPremium({ compact = false, className = '' }) {
  // Compact: inline badges pour header produit
  if (compact) {
    return (
      <div className={`flex flex-wrap gap-3 ${className}`}>
        {TRUST_ITEMS.slice(0, 4).map((item, i) => (
          <div key={`trust-${i}`} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
            <span style={{ color: item.iconColor }}>{item.emoji}</span>
            {item.title}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header section */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <span style={{
          display: 'inline-block',
          background: 'var(--teal-mist)',
          color: '#0B4B4E',
          border: '1px solid rgba(93,188,191,0.3)',
          borderRadius: 50,
          padding: '6px 16px',
          fontSize: 12,
          fontWeight: 600,
          marginBottom: 12,
        }}>
          Notre engagement
        </span>
        <h2 style={{
          fontFamily: "'Bodoni Moda', serif",
          fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
          fontWeight: 700,
          color: 'var(--ink)',
          letterSpacing: '-0.02em',
          margin: '0 0 10px',
        }}>
          Pourquoi nous choisir ?
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, maxWidth: 480, margin: '0 auto' }}>
          Depuis plusieurs années, Show Événement est le partenaire de confiance
          des familles et entreprises du Lot.
        </p>
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 18,
      }}>
        {TRUST_ITEMS.map((item, i) => (
          <TrustCard key={`trust-${i}`} item={item} index={i} />
        ))}
      </div>

      {/* Bottom badge */}
      <div style={{
        textAlign: 'center',
        marginTop: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}>
        <div style={{ display: 'flex', gap: 3 }}>
          {[1,2,3,4,5].map(s => (
            <Star key={s} size={16} fill={s <= 4 ? '#D4AF37' : 'transparent'} color="#D4AF37" />
          ))}
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
          <strong style={{ color: 'var(--ink)' }}>4.6/5</strong> — 9 avis vérifiés Google · Lot, Aveyron, Cantal
        </p>
      </div>
    </div>
  );
}

export { TRUST_ITEMS };
